import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { catchError, finalize, from, Observable, switchMap, throwError } from 'rxjs';

import Session from 'supertokens-web-js/recipe/session';
import { SUPER_TOKENS_READY } from '../auth/super-tokens-ready.token';

@Injectable()
export class SessionRefreshInterceptor implements HttpInterceptor {
  // Resolves after marketplace's AuthenticationService has finished
  // `SuperTokens.init()` (P0-11). Falls back to a resolved promise in
  // admin / SSR / tests where the SDK is already initialised by
  // bootstrap or where this interceptor never runs.
  private readonly superTokensReady = inject(SUPER_TOKENS_READY);

  private refreshInProgress = false;
  private refreshPromise: Promise<boolean> | null = null;

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(req).pipe(
      catchError((error: any) => {
        // Only 401 triggers a session refresh (authentication failure).
        // 403 means the server understood the credentials but the user is
        // not authorized for this resource (privacy codes like
        // invitation_required / password_required, or generic permission
        // denials). Refreshing the session won't change that, so retrying
        // would just waste a roundtrip and risk thrashing the auth flow.
        // if (error instanceof HttpErrorResponse && error.status === 401) {
        //   return this.handleAuthError(req, next, error);
        // }

        if (error instanceof HttpErrorResponse) {
          // Skip 403 with privacy codes - these should be handled by components
          if (error.status === 403 && error.error?.code) {
            return throwError(() => error);
          }
          // Handle 401 and other 403 errors (without code) for session refresh
          if (error.status === 401 || error.status === 403) {
            return this.handleAuthError(req, next, error);
          }
        }

        return throwError(() => error);
      })
    );
  }

  private handleAuthError(req: HttpRequest<any>, next: HttpHandler, originalError: HttpErrorResponse): Observable<HttpEvent<any>> {
    if (!this.refreshInProgress) {
      this.refreshInProgress = true;
      // Await SuperTokens init before any Session.* call — a 401 fired
      // during the brief bootstrap window (after AuthenticationService
      // kicks off init, before it resolves) would otherwise call into
      // an uninitialised SDK and throw.
      this.refreshPromise = this.superTokensReady.then(() => Session.attemptRefreshingSession());
    }

    return from(this.refreshPromise!).pipe(
      switchMap((refreshSuccess) => {
        if (refreshSuccess) {
          return next.handle(req); // Retry original request
        } else {
          // Session refresh failed - return the original error so components can handle it
          return throwError(() => originalError);
        }
      }),
      catchError(() => {
        // Refresh threw an error - return the original error
        return throwError(() => originalError);
      }),
      finalize(() => {
        this.refreshInProgress = false;
        this.refreshPromise = null;
      })
    );
  }
}
